/** Calendar view shared by the employer milestone and provider availability calendars. */
export type CalendarViewMode = 'month' | 'week';

// Calendar dates intentionally use local Y/M/D parts. Parsing YYYY-MM-DD with
// `new Date(value)` treats the value as UTC and can move it to the previous day.
export const toISODate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseISODate = (isoDate: string): Date => {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const addDays = (date: Date, amount: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);

export const startOfWeek = (date: Date): Date => addDays(date, -date.getDay());

export const startOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);

export const endOfMonth = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0);

/** A stable six-week month grid, padded to complete Sunday-Saturday weeks. */
export const buildMonthGrid = (anchor: Date): Date[] => {
  const first = startOfWeek(startOfMonth(anchor));
  return Array.from({ length: 42 }, (_, index) => addDays(first, index));
};

export const buildWeekDays = (anchor: Date): Date[] => {
  const first = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, index) => addDays(first, index));
};

/** Difference between local calendar days. Date.UTC avoids DST-length days. */
export const daysBetween = (from: Date, to: Date): number =>
  Math.round(
    (Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()) -
      Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())) /
      86_400_000
  );

export const buildDateRange = (fromISO: string, toISO: string): string[] => {
  const from = parseISODate(fromISO);
  const to = parseISODate(toISO);
  const first = from <= to ? from : to;
  const last = from <= to ? to : from;
  const length = daysBetween(first, last) + 1;
  return Array.from({ length }, (_, index) => toISODate(addDays(first, index)));
};

export const formatDisplayDate = (isoDate: string): string =>
  parseISODate(isoDate).toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

export const getBrowserTimezone = (): string | null => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezone?.trim() || null;
};
